'use client';

import { MyReviewElementDataType } from '@/components/sections/builder/elements/body/customer/customer_review/type';
import { usePaginationSearchParams } from '@/hooks/use-pagination';
import { useCustomerListReview } from '@/hooks/api/storefront/customer';
import Cookies from 'js-cookie';
import ModalImagePreview from '@/components/sections/builder/elements/body/product-detail/components/modal-image-preview';
import Pagination from '@/components/commons/pagination';
import { useState } from 'react';
import { StoreCustomerReviewItem } from '@/types/http';
import { Skeleton } from '@/components/ui/skeleton';
import { StarIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { FallbackImage } from '@/components/commons/fallback-image';
import Image from 'next/image';
import { format } from 'date-fns';
import { dummyReviewedItems } from '@/constant/dummyData/dummy-customer-review';

type listReviewedProps = {
  content?: MyReviewElementDataType;
  preview?: boolean;
  starColor?: string;
};

export default function ListReviewed({ preview, starColor }: listReviewedProps) {
  const token = Cookies.get('accessToken');
  const [params, setParams] = usePaginationSearchParams();

  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [initialImageIndex, setInitialImageIndex] = useState<number>(0);

  const { data: reviewedData, isLoading } = useCustomerListReview(
    token as string,
    {
      page: params.pageIndex + 1,
      pageSize: params.pageSize || 10,
    },
    {
      enabled: !preview,
    }
  );

  const displayData = preview
    ? {
        data: dummyReviewedItems,
        totalCount: dummyReviewedItems.length,
      }
    : reviewedData;

  const handleImagePreview = (review: StoreCustomerReviewItem, clickedImageUrl?: string) => {
    const reviewImages =
      review.media
        ?.filter((media) => media.media_type === 'image')
        .map((media) => media.media_url) || [];
    const imagesToShow = reviewImages.length > 0 ? reviewImages : [''];

    let startIndex = 0;
    if (clickedImageUrl) {
      const clickedIndex = imagesToShow.findIndex((img) => img === clickedImageUrl);
      startIndex = clickedIndex !== -1 ? clickedIndex : 0;
    }

    setPreviewImages(imagesToShow);
    setInitialImageIndex(startIndex);
    setOpenPreview(true);
  };

  return (
    <>
      <div className='mx-auto w-full p-6'>
        <div className='space-y-4'>
          {isLoading && !preview ? (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index}>
                  <div className='mb-4 flex gap-x-3'>
                    <Skeleton className='h-[50px] w-[50px] rounded-lg' />
                    <div className='flex flex-1 flex-col'>
                      <div className='flex gap-x-3'>
                        <Skeleton className='h-5 w-48' />
                      </div>

                      <div className='my-2 flex items-center gap-1'>
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                          <StarIcon
                            key={starIndex}
                            className='h-3 w-3'
                            style={{
                              color: starColor,
                              fill: starColor,
                            }}
                          />
                        ))}
                      </div>

                      <Skeleton className='mt-1 h-4 w-full' />
                      <Skeleton className='mt-1 h-4 w-3/4' />

                      <div className='mt-2 flex flex-wrap gap-2'>
                        {Array.from({ length: 3 }).map((_, imgIndex) => (
                          <Skeleton key={imgIndex} className='h-16 w-16 rounded-md' />
                        ))}
                      </div>

                      <div className='flex justify-end'>
                        <Skeleton className='mt-4 h-3 w-32' />
                      </div>
                    </div>
                  </div>
                  {index < 2 && <Separator className='my-4 border-gray-200' />}
                </div>
              ))}
            </>
          ) : (
            <>
              {!displayData?.data || displayData.data.length === 0 ? (
                <div className='flex justify-center py-8'>
                  <div className='text-gray-500'>No reviews found</div>
                </div>
              ) : (
                displayData.data.map((review: StoreCustomerReviewItem, index: number) => {
                  const isLastItem = index === displayData.data.length - 1;

                  return (
                    <div key={review.id}>
                      <div className='mb-4 flex gap-x-3'>
                        <div className='relative aspect-square h-[50px] w-[50px] rounded-lg'>
                          <FallbackImage
                            src={review.item_thumbnail_url || ''}
                            alt='image'
                            width={74}
                            height={74}
                            className='h-[50px] max-h-[50px] w-[50px] max-w-[50px] rounded object-contain object-center'
                          />
                        </div>
                        <div className='flex flex-1 flex-col'>
                          <div className='flex gap-x-3'>
                            <h1 className='text-sm font-semibold'>{review.product_name}</h1>
                          </div>

                          <div className='my-2 flex items-center gap-1'>
                            {Array.from({ length: 5 }).map((_, starIndex) => (
                              <StarIcon
                                key={starIndex}
                                className='h-4 w-4'
                                style={{
                                  color: starIndex < (review.rating || 0) ? starColor : '#E5E5E5',
                                  fill: starIndex < (review.rating || 0) ? starColor : '#E5E5E5',
                                }}
                              />
                            ))}
                          </div>

                          <p className='mt-1 text-sm text-gray-700'>{review.comment}</p>

                          {review.media &&
                            review.media.filter((media) => media.media_type === 'image').length >
                              0 && (
                              <div className='mt-2 flex flex-wrap gap-2'>
                                {review.media
                                  .filter((media) => media.media_type === 'image')
                                  .map((media, mediaIndex) => (
                                    <div
                                      key={mediaIndex}
                                      className='relative h-16 w-16 cursor-pointer overflow-hidden rounded-md border transition-opacity hover:opacity-80'
                                      onClick={() => handleImagePreview(review, media.media_url)}
                                    >
                                      <Image
                                        src={media.media_url}
                                        alt={`Review media ${mediaIndex + 1}`}
                                        fill
                                        className='object-cover'
                                      />
                                    </div>
                                  ))}
                              </div>
                            )}

                          <div className='flex justify-end'>
                            <span className='mt-4 text-xs text-muted-foreground'>
                              {format(new Date(review.created_at ?? ''), 'dd MMMM yyyy')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {!isLastItem && <Separator className='my-4 border-gray-200' />}
                    </div>
                  );
                })
              )}
            </>
          )}

          <Pagination
            currentPage={params.pageIndex + 1}
            pageSize={params.pageSize || 10}
            totalCount={displayData?.totalCount || 0}
            onPageChange={(page) => setParams((prev) => ({ ...prev, pageIndex: page - 1 }))}
            onPageSizeChange={(pageSize) =>
              setParams((prev) => ({ ...prev, pageSize, pageIndex: 0 }))
            }
          />
        </div>
      </div>

      {openPreview && (
        <ModalImagePreview
          images={previewImages}
          initialIndex={initialImageIndex}
          isOpen={openPreview}
          onClose={() => setOpenPreview(false)}
        />
      )}
    </>
  );
}
